import React, { useCallback, useMemo, useState } from "react";
import { FaTimes, FaExclamationTriangle, FaLock } from "react-icons/fa";
import { updateOrgVoiceSettings } from "../../api";
import {
  DEFAULT_LIMITS,
  DEFAULT_GREETING,
  backgroundSoundLabel,
  buildVoiceSettingsPatch,
  modelLabel,
  supportsExpressiveSpeech,
  toFormState,
  turnDetectionLabel,
  validateVoiceSettingsForm,
  whoSpeaksFirstLabel,
  TRANSFER_BRIEFING_LABELS,
} from "../../voiceSettings";
import TemplateField from "./TemplateField";
import VoicePicker from "./VoicePicker";

const SECTIONS = [
  { id: "voice", label: "Voice", hint: "Provider, voice, sound" },
  { id: "prompt", label: "Prompt", hint: "Greeting, tone, variables" },
  { id: "call", label: "Call behaviour", hint: "Turns, silence, interruptions" },
  { id: "voicemail", label: "Voicemail & transfer", hint: "Machines, forwarding" },
  { id: "analysis", label: "Analysis", hint: "Summary, sentiment" },
];

const BUILT_IN_TOOLS = [
  ["Send upload link", "Text the borrower the secure upload link", "active"],
  ["End call", "Hang up when the conversation is over", "active"],
  ["Take message", "Save a note for the loan officer", "coming"],
  ["Send SMS", "Text the borrower a confirmation", "coming"],
  ["Warm transfer", "Hand the call to a person, with a briefing", "active"],
  ["Knowledge base", "Answer from the organization's documents", "coming"],
];

const ERROR_SECTIONS = [
  ["voice", /speech recognition|language model|^voice|speaking speed|noise volume|background sound|keyword/i],
  ["prompt", /greeting|agent type|personality|prompt additions|time zone|first message|variables/i],
  ["call", /turn detection|response delay|endpointing|interruption|activation|silence|call length/i],
  ["voicemail", /voicemail|forwarding|transfer/i],
  ["analysis", /post-call|sentiment/i],
];

function sectionOfError(message) {
  const match = ERROR_SECTIONS.find(([, pattern]) => pattern.test(message));

  return match ? match[0] : "voice";
}

function Subsection({ title, children }) {
  return (
    <div className="oo-subsection">
      <h4 className="oo-subsection-title">{title}</h4>
      {children}
    </div>
  );
}

function Select({ label, help, value, options, onChange, render = (v) => v, disabled }) {
  return (
    <div className={`oo-field ${disabled ? "oo-field--disabled" : ""}`}>
      <label>{label}</label>
      {help ? <div className="oo-field-help">{help}</div> : null}
      <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {!options.some((option) => option === value || option?.id === value) ? (
          <option value={value}>{value || "Choose…"}</option>
        ) : null}
        {options.map((option) =>
          typeof option === "string" ? (
            <option key={option} value={option}>
              {render(option)}
            </option>
          ) : (
            <option key={option.id} value={option.id} disabled={option.disabled}>
              {option.label}
            </option>
          ),
        )}
      </select>
    </div>
  );
}

function NumberField({ label, help, value, bounds, step, onChange, disabled }) {
  return (
    <div className={`oo-field ${disabled ? "oo-field--disabled" : ""}`}>
      <label>
        {label} ({bounds.min}–{bounds.max})
      </label>
      {help ? <div className="oo-field-help">{help}</div> : null}
      <input
        type="number"
        min={bounds.min}
        max={bounds.max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function RangeField({ label, help, value, bounds, step, onChange, format = (v) => v }) {
  return (
    <div className="oo-field">
      <label>
        {label} <span className="oo-range-value">{format(value)}</span>
      </label>
      {help ? <div className="oo-field-help">{help}</div> : null}
      <input
        type="range"
        min={bounds.min}
        max={bounds.max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function ExplanationsEditor({ rules, standard, limits, onChange }) {
  const custom = Array.isArray(rules);
  const rows = custom ? rules : standard;
  const update = (index, key, value) => {
    const next = rows.map((rule) => ({ ...rule }));
    next[index] = { ...next[index], [key]: value };
    onChange(next);
  };
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const add = () => {
    if (rows.length < limits.maxEntries) {
      onChange([...rows.map((rule) => ({ ...rule })), { match: "", send: "" }]);
    }
  };

  return (
    <div className="oo-field">
      {rows.map((rule, index) => (
        <div className="oo-field-grid" key={index}>
          <input
            type="text"
            value={rule.match}
            maxLength={limits.matchMaxChars}
            placeholder="bank statement"
            onChange={(e) => update(index, "match", e.target.value)}
          />
          <input
            type="text"
            value={rule.send}
            maxLength={limits.sendMaxChars}
            placeholder="the most recent two months, every page"
            onChange={(e) => update(index, "send", e.target.value)}
          />
          <button type="button" className="oo-button oo-button--ghost" onClick={() => remove(index)}>
            Remove
          </button>
        </div>
      ))}
      <div className="oo-dialog-actions">
        <button type="button" className="oo-button oo-button--ghost" onClick={add} disabled={rows.length >= limits.maxEntries}>
          Add a rule
        </button>
        <button type="button" className="oo-button oo-button--ghost" onClick={() => onChange(null)} disabled={!custom}>
          Use the standard list
        </button>
        <span className="oo-field-help">
          {rows.length} / {limits.maxEntries}
          {custom ? "" : " · standard list"}
        </span>
      </div>
    </div>
  );
}

function Toggle({ label, help, checked, onChange, disabled }) {
  return (
    <label className={`oo-toggle ${disabled ? "oo-field--disabled" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        {label}
        {help ? <div className="oo-field-help">{help}</div> : null}
      </span>
    </label>
  );
}

/**
 * The voice assistant configuration, in sections: voice, prompt, call
 * behaviour, voicemail and transfer, analysis. Validated here with the same
 * rules the backend applies, so a mistake is shown before the round trip.
 */
export default function VoiceSettingsDialog({ organizationId, env, data, orgName, onClose, onSaved }) {
  const [form, setForm] = useState(() => toFormState(data.settings));
  const [section, setSection] = useState("voice");
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const options = useMemo(() => data.options || {}, [data.options]);
  const limits = useMemo(() => ({ ...DEFAULT_LIMITS, ...(data.limits || {}) }), [data.limits]);
  const variables = options.variables?.[form.agentType] || [];
  const variableDetails = options.variableDetails?.[form.agentType] || [];
  const voices = options.voices || [];
  const providers = options.voiceProviders?.length
    ? options.voiceProviders
    : [...new Set(voices.map((voice) => voice.provider))].map((id) => ({ id, label: id }));

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    const validationErrors = validateVoiceSettingsForm(form, options, limits);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setSection(sectionOfError(validationErrors[0]));
      return;
    }
    setErrors([]);
    setSaving(true);
    setSaveError(null);
    try {
      const json = await updateOrgVoiceSettings(organizationId, buildVoiceSettingsPatch(form), env);
      onSaved(json?.settings || null);
      onClose();
    } catch (err) {
      setSaveError(err?.message || "Failed to save voice settings");
    } finally {
      setSaving(false);
    }
  }, [form, options, limits, organizationId, env, onSaved, onClose]);

  const agentTypeOptions = (options.agentTypes || []).map((type) => ({
    id: type.id,
    label: type.status === "active" ? type.label : `${type.label} (beta)`,
    disabled: type.status !== "active",
  }));
  const presetOptions = [
    { id: "", label: "None" },
    ...(options.personalityPresets || []).map((preset) => ({
      id: preset.id,
      label: `${preset.label} — ${String(preset.text || "").split(". ")[0]}`,
    })),
  ];
  const soundOptions = [
    ...(options.backgroundSounds || ["off"]).map((sound) => ({ id: sound, label: backgroundSoundLabel(sound) })),
    ...(options.unavailableBackgroundSounds || []).map((sound) => ({
      id: sound,
      label: `${backgroundSoundLabel(sound)} (needs an audio file)`,
      disabled: true,
    })),
  ];

  return (
    <div className="oo-modal-overlay" role="dialog" aria-modal="true">
      <div className="oo-modal oo-modal--wide">
        <div className="oo-modal-head">
          <h3>Voice assistant · {orgName}</h3>
          <button type="button" className="oo-modal-close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="oo-modal-layout">
          <nav className="oo-modal-nav" aria-label="Settings sections">
            {SECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={section === item.id ? "active" : ""}
                onClick={() => setSection(item.id)}
              >
                {item.label}
                <small>{item.hint}</small>
              </button>
            ))}
          </nav>

          <div className="oo-modal-pane">
            {section === "voice" ? (
              <>
                <Subsection title="Ear and brain">
                  <div className="oo-field-grid">
                    <Select
                      label="Speech recognition (the ear)"
                      value={form.stt}
                      options={options.models?.stt || []}
                      render={modelLabel}
                      onChange={(value) => setField("stt", value)}
                    />
                    <Select
                      label="Language model (the brain)"
                      value={form.llm}
                      options={options.models?.llm || []}
                      render={modelLabel}
                      onChange={(value) => setField("llm", value)}
                    />
                  </div>
                </Subsection>
                <Subsection title="Voice (the mouth)">
                  {voices.length ? (
                    <VoicePicker
                      voices={voices}
                      providers={providers}
                      value={form.voiceKey}
                      onChange={(value) => setField("voiceKey", value)}
                      env={env}
                      previewSentence={options.previewSentence}
                    />
                  ) : (
                    <div className="oo-field-help">
                      The voice list could not be loaded from the voice service, so the voice cannot be changed right now.
                    </div>
                  )}
                  <RangeField
                    label="Speaking speed"
                    help="1.0 is normal. Applied by xAI and Azure voices."
                    value={form.speed}
                    bounds={limits.speed}
                    step={0.05}
                    format={(v) => `${Number(v).toFixed(2)}×`}
                    onChange={(value) => setField("speed", value)}
                  />
                  <Toggle
                    label="Expressive speech"
                    help={
                      supportsExpressiveSpeech(voices.find((voice) => voice.key === form.voiceKey))
                        ? "The assistant may add a short pause, a breath or a softer tone where a person would. Transcripts never show the marks."
                        : "Only xAI voices understand delivery marks; this has no effect on the selected voice."
                    }
                    checked={form.expressiveSpeech}
                    onChange={(value) => setField("expressiveSpeech", value)}
                  />
                </Subsection>
                <Subsection title="Background sound">
                  <div className="oo-field-grid">
                    <Select
                      label="Background sound"
                      help="A soft ambience so the assistant does not sound like it is calling from a vacuum."
                      value={form.backgroundSound}
                      options={soundOptions}
                      onChange={(value) => setField("backgroundSound", value)}
                    />
                    <RangeField
                      label="Noise volume"
                      value={form.backgroundVolume}
                      bounds={limits.backgroundVolume}
                      step={0.01}
                      format={(v) => Number(v).toFixed(2)}
                      onChange={(value) => setField("backgroundVolume", value)}
                    />
                  </div>
                  <Toggle
                    label="Play a soft typing sound while the assistant thinks"
                    checked={form.thinkingSound}
                    onChange={(value) => setField("thinkingSound", value)}
                  />
                </Subsection>
                <Subsection title="Keyword boosting">
                  <div className="oo-field">
                    <label>Words the recogniser should expect (comma-separated, up to {limits.keyTerms.maxEntries})</label>
                    <div className="oo-field-help">
                      Lender and product names, document names the borrowers use. The loan's own names are added automatically. Deepgram models only.
                    </div>
                    <textarea
                      rows={2}
                      value={form.keyTerms}
                      onChange={(e) => setField("keyTerms", e.target.value)}
                      placeholder="Newfi, CAIVRS, HELOC"
                    />
                  </div>
                </Subsection>
              </>
            ) : null}

            {section === "prompt" ? (
              <>
                <Subsection title="Greeting">
                  <div className="oo-field-grid">
                    <Select
                      label="Who speaks first?"
                      help="On a phone call the borrower usually says hello first. The assistant can speak first instead, after the pickup is judged a person when voicemail detection is on."
                      value={form.whoSpeaksFirst}
                      options={options.whoSpeaksFirst || ["borrower", "assistant"]}
                      render={whoSpeaksFirstLabel}
                      onChange={(value) => setField("whoSpeaksFirst", value)}
                    />
                    <NumberField
                      label="First message delay (seconds)"
                      help="Only when the assistant speaks first."
                      value={form.firstMessageDelayS}
                      bounds={limits.firstMessageDelayS}
                      step={0.1}
                      disabled={form.whoSpeaksFirst !== "assistant"}
                      onChange={(value) => setField("firstMessageDelayS", value)}
                    />
                  </div>
                  <TemplateField
                    label="Greeting message"
                    help={`Leave empty for the standard line: “${DEFAULT_GREETING}”. The greeting must ask for {{first_name}}; click a variable to insert it.`}
                    value={form.greeting}
                    onChange={(value) => setField("greeting", value)}
                    variables={variables}
                    variableDetails={variableDetails}
                    maxLength={limits.greetingMaxChars}
                    placeholder="{{greeting}}, this is Mia, an AI assistant calling for {{loan_officer_name}} at {{brand_name}}. Is this {{first_name}}?"
                  />
                </Subsection>
                <Subsection title="Agent">
                  <div className="oo-field-grid">
                    <Select
                      label="Agent type"
                      help="Only Document request is active; the others are in beta."
                      value={form.agentType}
                      options={agentTypeOptions}
                      onChange={(value) => setField("agentType", value)}
                    />
                    <Select
                      label="Agent personality"
                      value={form.personalityPreset}
                      options={presetOptions}
                      onChange={(value) => setField("personalityPreset", value)}
                    />
                  </div>
                  <div className="oo-field">
                    <label>Custom tone / persona (optional, up to {limits.personalityMaxChars} characters)</label>
                    <input
                      type="text"
                      value={form.personality}
                      maxLength={limits.personalityMaxChars}
                      onChange={(e) => setField("personality", e.target.value)}
                      placeholder="e.g. Warm but efficient; never pushy"
                    />
                  </div>
                  <TemplateField
                    label="Prompt additions"
                    help="Added to the standard prompt after the document list. The safety rules always apply."
                    value={form.promptAdditions}
                    onChange={(value) => setField("promptAdditions", value)}
                    variables={variables}
                    variableDetails={variableDetails}
                    maxLength={limits.promptAdditionsMaxChars}
                    rows={5}
                    placeholder="Mention that the office is open 9 to 5 Eastern. If they ask about rates, say {{loan_officer_name}} will call them."
                  />
                  <Toggle
                    label="Defer figures to team"
                    help="Hard-block quoting rates, payments or qualification numbers; the assistant always defers to a licensed team member."
                    checked={form.deferFigures}
                    onChange={(value) => setField("deferFigures", value)}
                  />
                  <Toggle
                    label="Confirm the property street before any loan detail"
                    help="After the borrower's name, the assistant asks for the street of the property and goes on only if it matches the loan. Only when the loan has an address."
                    checked={form.identityCheck}
                    onChange={(value) => setField("identityCheck", value)}
                  />
                </Subsection>
                <Subsection title="What to send">
                  <div className="oo-field-help">
                    One line per document type, spoken after the document's name so the borrower sends the right thing first time. A rule applies when its phrase appears in the document's name; the first match wins. Up to {limits.documentExplanations.maxEntries} rules.
                  </div>
                  <ExplanationsEditor
                    rules={form.documentExplanations}
                    standard={options.documentExplanations?.defaults || []}
                    limits={limits.documentExplanations}
                    onChange={(value) => setField("documentExplanations", value)}
                  />
                </Subsection>
                <Subsection title="Agent time zone">
                  <div className="oo-field">
                    <label>Time zone (IANA name)</label>
                    <div className="oo-field-help">
                      Anchors {"{{greeting}}"}, {"{{current_date}}"} and {"{{current_time}}"}, the calling-hours gate and how the assistant understands times the borrower mentions, when the loan has no property state.
                    </div>
                    <input
                      type="text"
                      value={form.timezone}
                      onChange={(e) => setField("timezone", e.target.value)}
                      placeholder="America/New_York"
                    />
                  </div>
                </Subsection>
                <Subsection title="Tools">
                  <div className="oo-field-help">
                    Built-in tools the assistant may use. The ones marked coming are planned; custom tools (approved hosts only) follow.
                  </div>
                  <div className="oo-field-row">
                    {BUILT_IN_TOOLS.map(([name, description, status]) => (
                      <label key={name} className={`oo-toggle ${status !== "active" ? "oo-field--disabled" : ""}`}>
                        <input type="checkbox" checked={status === "active"} disabled readOnly />
                        <span>
                          {name}
                          {status !== "active" ? <span className="oo-beta">coming</span> : null}
                          <div className="oo-field-help">{description}</div>
                        </span>
                      </label>
                    ))}
                  </div>
                </Subsection>
                <div className="oo-locked-note">
                  <FaLock />
                  <span>
                    Always on, not configurable: the assistant says it is an AI assistant whenever asked, calls only inside legal calling hours, and never asks for account numbers, social security numbers or passwords.
                  </span>
                </div>
              </>
            ) : null}

            {section === "call" ? (
              <>
                <Subsection title="Turns">
                  <div className="oo-field-grid">
                    <Select
                      label="Turn detection"
                      value={form.turnDetection}
                      options={options.turnDetection || ["stt", "vad"]}
                      render={turnDetectionLabel}
                      onChange={(value) => setField("turnDetection", value)}
                    />
                    <NumberField
                      label="Min response delay (seconds)"
                      help="How long the assistant waits after the borrower seems done before answering."
                      value={form.endpointingMinDelayS}
                      bounds={limits.endpointingMinDelayS}
                      step={0.1}
                      onChange={(value) => setField("endpointingMinDelayS", value)}
                    />
                    <NumberField
                      label="Max endpointing delay (seconds)"
                      value={form.endpointingMaxDelayS}
                      bounds={limits.endpointingMaxDelayS}
                      step={0.1}
                      onChange={(value) => setField("endpointingMaxDelayS", value)}
                    />
                    <RangeField
                      label="Background activation (VAD)"
                      help="How loud a sound must be to count as the borrower speaking."
                      value={form.vadActivationThreshold}
                      bounds={limits.vadActivationThreshold}
                      step={0.05}
                      format={(v) => Number(v).toFixed(2)}
                      onChange={(value) => setField("vadActivationThreshold", value)}
                    />
                  </div>
                  <Toggle
                    label="Preemptive speech (Flux only)"
                    help="Start preparing the reply during the borrower's pause for snappier turns, at the cost of an occasional restart if they keep talking."
                    checked={form.preemptiveSpeech}
                    onChange={(value) => setField("preemptiveSpeech", value)}
                  />
                </Subsection>
                <Subsection title="Interruptions">
                  <div className="oo-field-grid">
                    <NumberField
                      label="Min interruption words"
                      help="Words the borrower must say before the assistant stops talking (0 = any sound)."
                      value={form.interruptionMinWords}
                      bounds={limits.interruptionMinWords}
                      step={1}
                      onChange={(value) => setField("interruptionMinWords", value)}
                    />
                    <NumberField
                      label="Min interruption duration (seconds)"
                      value={form.interruptionMinDurationS}
                      bounds={limits.interruptionMinDurationS}
                      step={0.1}
                      onChange={(value) => setField("interruptionMinDurationS", value)}
                    />
                  </div>
                </Subsection>
                <Subsection title="Silence and length">
                  <div className="oo-field-grid">
                    <NumberField
                      label="Initial silence timeout (seconds)"
                      help="No word from the borrower at all after the call connects."
                      value={form.initialSilenceTimeoutS}
                      bounds={limits.initialSilenceTimeoutS}
                      step={1}
                      onChange={(value) => setField("initialSilenceTimeoutS", value)}
                    />
                    <NumberField
                      label="Final silence timeout (seconds)"
                      help="Silence after the borrower has spoken."
                      value={form.silenceTimeoutS}
                      bounds={limits.silenceTimeoutS}
                      step={1}
                      onChange={(value) => setField("silenceTimeoutS", value)}
                    />
                    <NumberField
                      label="Silence check-ins before hanging up"
                      value={form.maxSilenceNudges}
                      bounds={limits.maxSilenceNudges}
                      step={1}
                      onChange={(value) => setField("maxSilenceNudges", value)}
                    />
                    <NumberField
                      label="Call length cap (seconds)"
                      value={form.maxDurationS}
                      bounds={limits.maxDurationS}
                      step={10}
                      onChange={(value) => setField("maxDurationS", value)}
                    />
                  </div>
                </Subsection>
              </>
            ) : null}

            {section === "voicemail" ? (
              <>
                <Subsection title="Voicemail">
                  <Toggle
                    label="Voicemail detection"
                    help="Recognise answering machines; a voicemail is never counted as an answered call."
                    checked={form.voicemailDetect}
                    onChange={(value) => setField("voicemailDetect", value)}
                  />
                  <Toggle
                    label="Leave voicemail"
                    help="On outbound, drop a message if a machine answers."
                    checked={form.voicemailLeaveMessage}
                    disabled={!form.voicemailDetect}
                    onChange={(value) => setField("voicemailLeaveMessage", value)}
                  />
                  <TemplateField
                    label="Voicemail message"
                    help="Played when a voicemail greeting is detected, then the call ends. Keep it under about twenty seconds."
                    value={form.voicemailMessage}
                    onChange={(value) => setField("voicemailMessage", value)}
                    variables={variables}
                    variableDetails={variableDetails}
                    maxLength={limits.voicemailMessageMaxChars}
                    rows={3}
                    disabled={!form.voicemailLeaveMessage}
                    placeholder="Hi {{first_name}}, this is {{agent_name}}, an AI assistant calling on behalf of {{loan_officer_name}}. Please call them back when you have a moment."
                  />
                </Subsection>
                <Subsection title="Recording">
                  <Toggle
                    label="Record calls"
                    help="One stereo audio file per answered call (assistant left, borrower right), kept 90 days in Addy's storage. Staff listen from the voice console."
                    checked={form.recording}
                    onChange={(value) => setField("recording", value)}
                  />
                  <Toggle
                    label="Say the recording notice"
                    help='Adds "This call may be recorded for quality." as the second sentence of the greeting on recorded calls. The wording is fixed.'
                    checked={form.consentLine}
                    disabled={!form.recording}
                    onChange={(value) => setField("consentLine", value)}
                  />
                </Subsection>
                <Subsection title="Warm transfer">
                  <div className="oo-field-grid">
                    <Select
                      label="Warm transfer briefing"
                      value={form.transferBriefing}
                      options={options.transferBriefings || ["auto", "custom"]}
                      render={(v) => TRANSFER_BRIEFING_LABELS[v] || v}
                      onChange={(value) => setField("transferBriefing", value)}
                    />
                    <div className="oo-field">
                      <label>Transfer destination name</label>
                      <div className="oo-field-help">
                        Who the borrower is told they are being put through to. Leave blank for “someone who can help”.
                      </div>
                      <input
                        type="text"
                        value={form.destinationName}
                        maxLength={limits.destinationNameMaxChars}
                        onChange={(e) => setField("destinationName", e.target.value)}
                        placeholder="e.g. Jeff, or one of our loan officers"
                      />
                    </div>
                  </div>
                </Subsection>
                <Subsection title="Forwarding number and hours">
                  <div className="oo-field-help">
                    A forwarding number switches the warm transfer on. Hours are in the organization's time zone (Prompt section); empty means any time. Outside them the assistant offers a call back instead of dialling.
                  </div>
                  <div className="oo-field-grid">
                    <div className="oo-field">
                      <label>Forwarding phone number (+E.164)</label>
                      <input
                        type="text"
                        value={form.forwardingNumber}
                        onChange={(e) => setField("forwardingNumber", e.target.value)}
                        placeholder="+12485550142"
                      />
                    </div>
                    <Toggle
                      label="Forward inbound calls immediately"
                      help="A borrower who calls back is put through before any conversation; if nobody answers, the assistant carries on with the documents. Outbound calls ignore this."
                      checked={form.forwardImmediately}
                      onChange={(value) => setField("forwardImmediately", value)}
                    />
                    <div className="oo-field">
                      <label>Transfer hours from</label>
                      <input
                        type="time"
                        value={form.transferHoursStart}
                        onChange={(e) => setField("transferHoursStart", e.target.value)}
                      />
                    </div>
                    <div className="oo-field">
                      <label>Transfer hours until</label>
                      <input
                        type="time"
                        value={form.transferHoursEnd}
                        onChange={(e) => setField("transferHoursEnd", e.target.value)}
                      />
                    </div>
                  </div>
                </Subsection>
              </>
            ) : null}

            {section === "analysis" ? (
              <Subsection title="After each call">
                <Toggle
                  label="Post-call analysis"
                  help="Generate a summary after each call: what was agreed, which documents, follow-ups."
                  checked={form.postCall}
                  onChange={(value) => setField("postCall", value)}
                />
                <Toggle
                  label="Sentiment analysis"
                  help="Score the borrower's sentiment (positive, neutral, negative) in the post-call analysis."
                  checked={form.sentiment}
                  disabled={!form.postCall}
                  onChange={(value) => setField("sentiment", value)}
                />
              </Subsection>
            ) : null}

          </div>
        </div>

        {errors.length > 0 || saveError ? (
          <div className="oo-modal-errors" role="alert">
            {errors.length > 0 ? (
              <div className="oo-field-help">
                Not saved. Fix these first; a field may sit in another section.
              </div>
            ) : null}
            <div className="oo-form-errors">
              {errors.map((err) => (
                <div key={err}>
                  <FaExclamationTriangle /> {err}
                  {sectionOfError(err) !== section ? (
                    <button
                      type="button"
                      className="oo-link-btn"
                      onClick={() => setSection(sectionOfError(err))}
                    >
                      open {SECTIONS.find((item) => item.id === sectionOfError(err))?.label}
                    </button>
                  ) : null}
                </div>
              ))}
              {saveError ? (
                <div>
                  <FaExclamationTriangle /> Not saved: {saveError}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="oo-modal-foot">
          <div className="oo-field-help">
            {data.optionsFromVoiceApi === false
              ? "The voice service could not be reached; voices and variables shown are limited."
              : "Changes apply to the next call this organization places."}
          </div>
          <div className="oo-modal-foot-right">
            <button type="button" className="oo-mini-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="oo-mini-btn oo-mini-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save settings"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
